import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { comparators, types } from 'components/scenarios/utils/criteria-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { defaultPredicatesValue } from 'utils/team-external-integrations/team-external-integrations-util'

export const StatusCheckPredicateConfiguration: React.FC = () => {
  const { control, setValue } = useFormContext<StatusCheckFormValues>()
  const { isStatusCheckConnectionValidated } = useStatusCheckDetailsContext()
  const fields = useWatch<
    StatusCheckFormValues,
    'evaluationConfiguration.responseBodyEvaluation.predicates'
  >({ name: 'evaluationConfiguration.responseBodyEvaluation.predicates' })
  const hasValidPredicates = Boolean(fields?.length)

  /*
   * Our default value for `predicates` is `[]`. We cannot determine at the point of default value assignment
   * whether the predicates field should be displayed or not, so we cannot conditionally set the appropriate
   * default value at that time. Thus, if we wish to see any output, we need to check whether the fields array
   * is empty, and if so, fall back to the default predicates value. (which should render an empty fieldset)
   */
  useEffect(() => {
    if (hasValidPredicates) return

    setValue(
      'evaluationConfiguration.responseBodyEvaluation.predicates',
      defaultPredicatesValue
    )
  }, [hasValidPredicates, setValue])

  // In this case, we will set the `predicates` field to the default in the `useEffect`
  // hook above, and properly display the fieldset in the next rendering pass.
  if (!hasValidPredicates) return null

  return (
    <Grid container direction="column" rowSpacing={2}>
      {fields?.map((field, index) => {
        const predicateType = field.type
        const comparatorValues = predicateType
          ? types[predicateType].comparators
          : undefined

        return (
          <Grid
            key={`${field.toString()}-${index}`}
            container
            item
            direction="row"
            spacing={2}
            sx={{ flexWrap: 'nowrap' }}
          >
            <Grid item>
              <FormControl>
                <Controller
                  control={control}
                  name={`evaluationConfiguration.responseBodyEvaluation.predicates.${index}.jpQuery`}
                  render={({ field: controllerRenderField }) => (
                    <TextField
                      {...controllerRenderField}
                      disabled={!isStatusCheckConnectionValidated}
                      helperText="Key from response body"
                      label="Key"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 135 }}>
                <InputLabel>Key Type</InputLabel>
                <Controller
                  control={control}
                  name={`evaluationConfiguration.responseBodyEvaluation.predicates.${index}.type`}
                  render={({ field: controllerRenderField }) => (
                    <Select
                      {...controllerRenderField}
                      disabled={!isStatusCheckConnectionValidated}
                      label="Key Type"
                      variant="outlined"
                    >
                      {Object.keys(types).map((predicateType) => (
                        <MenuItem
                          key={predicateType}
                          data-testid={`shared-status-check-evaluation-predicate-type-${predicateType.toLowerCase()}`}
                          value={predicateType}
                        >
                          {predicateType}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 135 }}>
                <InputLabel>Comparator</InputLabel>
                <Controller
                  control={control}
                  name={`evaluationConfiguration.responseBodyEvaluation.predicates.${index}.comparator`}
                  render={({ field: controllerRenderField }) => (
                    <Select
                      {...controllerRenderField}
                      disabled={
                        !isStatusCheckConnectionValidated || !comparatorValues
                      }
                      label="Comparator"
                      variant="outlined"
                    >
                      {comparatorValues?.map((comparator) => (
                        <MenuItem
                          key={comparator}
                          data-testid={`shared-status-check-evaluation-predicate-key-comparator-${comparator.toLowerCase()}`}
                          value={comparator}
                        >
                          {comparators[comparator].label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl>
                <Controller
                  control={control}
                  name={`evaluationConfiguration.responseBodyEvaluation.predicates.${index}.rValue`}
                  render={({ field: controllerRenderField }) => (
                    <TextField
                      {...controllerRenderField}
                      disabled={!isStatusCheckConnectionValidated}
                      helperText="Success Value from response body"
                      label="Value"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        )
      })}
    </Grid>
  )
}
